import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Grid
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import axiosInstance from '../../utils/axiosConfig';

interface InvoiceEntry {
  _id: string;
  invoiceId: {
    _id: string;
    invoiceNumber: string;
    date: string;
    pageId?: string; // إضافة pageId
  };
  invoiceNumber: string;
  invoiceType: string;
  accountId: {
    _id: string;
    name: string;
    accountNumber: string;
  };
  accountName: string;
  accountSource: 'customer_supplier' | 'other';
  amount: number;
  valueType: string;
  accountType: 'debit' | 'credit';
  status: 'pending' | 'completed' | 'failed';
  errorMessage?: string;
  createdAt: string;
  createdBy?: {
    _id: string;
    username: string;
  };
}

// واجهة القيود المجمعة حسب الفاتورة
interface GroupedInvoiceEntry {
  invoiceNumber: string;
  invoiceId: string;
  pageId: string; // إضافة pageId
  invoiceDate: string;
  invoiceType: string;
  entriesCount: number;
  totalDebit: number;
  totalCredit: number;
  status: 'completed' | 'partial' | 'failed';
  entries: InvoiceEntry[];
}

const InvoiceEntriesPage: React.FC = () => {
  const [entries, setEntries] = useState<InvoiceEntry[]>([]);
  const [groupedEntries, setGroupedEntries] = useState<GroupedInvoiceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState<string>('');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedEntry, setSelectedEntry] = useState<InvoiceEntry | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // جلب القيود
  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: page + 1,
        limit: 1000 // جلب عدد كبير لتجميعها
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await axiosInstance.get('/invoice-entries/all', { params });
      
      const fetchedEntries = response.data.entries || [];
      setEntries(fetchedEntries);
      
      // تجميع القيود حسب رقم الفاتورة
      const grouped = groupEntriesByInvoice(fetchedEntries);
      setGroupedEntries(grouped);
      setTotal(grouped.length);
    } catch (err: any) {
      console.error('خطأ في جلب القيود:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء جلب القيود');
    } finally {
      setLoading(false);
    }
  };

  // تجميع القيود حسب رقم الفاتورة
  const groupEntriesByInvoice = (entries: InvoiceEntry[]): GroupedInvoiceEntry[] => {
    const grouped = new Map<string, GroupedInvoiceEntry>();

    entries.forEach(entry => {
      const invoiceNumber = entry.invoiceNumber;
      
      if (!grouped.has(invoiceNumber)) {
        grouped.set(invoiceNumber, {
          invoiceNumber,
          invoiceId: entry.invoiceId?._id || '',
          pageId: entry.invoiceId?.pageId || '', // إضافة pageId
          invoiceDate: entry.invoiceId?.date || entry.createdAt,
          invoiceType: entry.invoiceType,
          entriesCount: 0,
          totalDebit: 0,
          totalCredit: 0,
          status: 'completed',
          entries: []
        });
      }

      const group = grouped.get(invoiceNumber)!;
      group.entries.push(entry);
      group.entriesCount++;
      
      // حساب المجاميع
      if (entry.accountType === 'debit') {
        group.totalDebit += entry.amount;
      } else {
        group.totalCredit += entry.amount;
      }

      // تحديد الحالة
      if (entry.status === 'failed') {
        group.status = 'failed';
      } else if (entry.status === 'pending' && group.status !== 'failed') {
        group.status = 'partial';
      }
    });

    return Array.from(grouped.values()).sort((a, b) =>
      new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime()
    );
  };

  // تطبيق الفلاتر
  const getFilteredEntries = (): GroupedInvoiceEntry[] => {
    let filtered = [...groupedEntries];

    // فلتر رقم الفاتورة
    if (invoiceNumberFilter) {
      filtered = filtered.filter(entry =>
        entry.invoiceNumber.toLowerCase().includes(invoiceNumberFilter.toLowerCase())
      );
    }

    // فلتر نوع الفاتورة
    if (invoiceTypeFilter) {
      filtered = filtered.filter(entry => entry.invoiceType === invoiceTypeFilter);
    }

    // فلتر التاريخ من
    if (dateFrom) {
      const startOfDay = new Date(dateFrom);
      startOfDay.setHours(0, 0, 0, 0);
      filtered = filtered.filter(entry =>
        new Date(entry.invoiceDate) >= startOfDay
      );
    }

    // فلتر التاريخ إلى - يحترم نهاية اليوم
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(entry =>
        new Date(entry.invoiceDate) <= endOfDay
      );
    }

    return filtered;
  };

  useEffect(() => {
    fetchEntries();
  }, [statusFilter]);

  // مسح الفلاتر
  const handleClearFilters = () => {
    setInvoiceNumberFilter('');
    setInvoiceTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setPage(0);
  };

  // توسيع/طي الصف
  const toggleRow = (invoiceNumber: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(invoiceNumber)) {
      newExpanded.delete(invoiceNumber);
    } else {
      newExpanded.add(invoiceNumber);
    }
    setExpandedRows(newExpanded);
  };

  // حذف قيد
  const handleDelete = async (entryId: string) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا القيد؟')) {
      return;
    }

    try {
      await axiosInstance.delete(`/invoice-entries/${entryId}`);
      fetchEntries();
    } catch (err: any) {
      console.error('خطأ في حذف القيد:', err);
      alert(err.response?.data?.message || 'حدث خطأ أثناء حذف القيد');
    }
  };

  // إعادة محاولة قيد فاشل
  const handleRetry = async (entryId: string) => {
    try {
      await axiosInstance.post(`/invoice-entries/retry/${entryId}`);
      fetchEntries();
    } catch (err: any) {
      console.error('خطأ في إعادة المحاولة:', err);
      alert(err.response?.data?.message || 'حدث خطأ أثناء إعادة المحاولة');
    }
  };

  // عرض تفاصيل القيد
  const handleViewDetails = (entry: InvoiceEntry) => {
    setSelectedEntry(entry);
    setDetailsDialogOpen(true);
  };

  // تنسيق نوع الفاتورة
  const getInvoiceTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      sales_invoice: 'فاتورة مبيعات',
      sales_return: 'مرتجع مبيعات',
      purchase_invoice: 'فاتورة مشتريات',
      purchase_return: 'مرتجع مشتريات',
      quotation: 'عرض سعر',
      beginning_inventory: 'بضاعة أول المدة',
      import_invoice_return: 'مرتجع فاتورة استيراد'
    };
    return types[type] || type;
  };

  // تنسيق نوع القيمة
  const getValueTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      total_with_tax: 'إجمالي الفاتورة شامل الضريبة',
      subtotal_before_discount: 'المجموع الفرعي قبل الخصم',
      subtotal_after_discount: 'المجموع الفرعي بعد الخصم',
      tax_amount: 'الضريبة',
      total_discount: 'الخصم الكلي',
      total_discount_before_tax: 'الخصم الكلي قبل الضريبة'
    };
    return types[type] || type;
  };

  // تنسيق حالة القيد
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip label="مكتمل" color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'failed':
        return <Chip label="فاشل" color="error" size="small" icon={<ErrorIcon />} />;
      case 'pending':
        return <Chip label="قيد الانتظار" color="warning" size="small" icon={<PendingIcon />} />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  // الحصول على البيانات المفلترة والمقسمة للصفحة الحالية
  const filteredEntries = getFilteredEntries();
  const paginatedEntries = filteredEntries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              قيود الفواتير التلقائية
            </Typography>
            <Typography variant="body2" color="text.secondary">
              عرض وإدارة القيود المحاسبية المنشأة تلقائياً من الفواتير
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchEntries}
            disabled={loading}
          >
            تحديث
          </Button>
        </Box>

        {/* الفلاتر */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="رقم الفاتورة"
              value={invoiceNumberFilter}
              onChange={(e) => {
                setInvoiceNumberFilter(e.target.value);
                setPage(0);
              }}
              placeholder="ابحث برقم الفاتورة"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>نوع الفاتورة</InputLabel>
              <Select
                value={invoiceTypeFilter}
                onChange={(e) => {
                  setInvoiceTypeFilter(e.target.value);
                  setPage(0);
                }}
                label="نوع الفاتورة"
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="sales_invoice">فاتورة مبيعات</MenuItem>
                <MenuItem value="sales_return">مرتجع مبيعات</MenuItem>
                <MenuItem value="purchase_invoice">فاتورة مشتريات</MenuItem>
                <MenuItem value="purchase_return">مرتجع مشتريات</MenuItem>
                <MenuItem value="quotation">عرض سعر</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>الحالة</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="الحالة"
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="completed">مكتمل</MenuItem>
                <MenuItem value="failed">فاشل</MenuItem>
                <MenuItem value="pending">قيد الانتظار</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="من تاريخ"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="إلى تاريخ"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              size="medium"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{ height: '40px' }}
            >
              مسح
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>رقم الفاتورة</TableCell>
                <TableCell>نوع الفاتورة</TableCell>
                <TableCell>التاريخ</TableCell>
                <TableCell align="center">عدد القيود</TableCell>
                <TableCell align="right">إجمالي المدين</TableCell>
                <TableCell align="right">إجمالي الدائن</TableCell>
                <TableCell align="center">الحالة</TableCell>
                <TableCell align="center">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      لا توجد قيود
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedEntries.map((group) => (
                  <React.Fragment key={group.invoiceNumber}>
                    <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(group.invoiceNumber)}
                        >
                          {expandedRows.has(group.invoiceNumber) ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {group.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{getInvoiceTypeLabel(group.invoiceType)}</TableCell>
                      <TableCell>
                        {format(new Date(group.invoiceDate), 'dd/MM/yyyy', { locale: ar })}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={group.entriesCount} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium" color="primary.main">
                          {group.totalDebit.toLocaleString('ar-SA', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} ر.س
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium" color="secondary.main">
                          {group.totalCredit.toLocaleString('ar-SA', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} ر.س
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {group.status === 'completed' && (
                          <Chip label="مكتمل" color="success" size="small" icon={<CheckCircleIcon />} />
                        )}
                        {group.status === 'failed' && (
                          <Chip label="فاشل" color="error" size="small" icon={<ErrorIcon />} />
                        )}
                        {group.status === 'partial' && (
                          <Chip label="جزئي" color="warning" size="small" icon={<PendingIcon />} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="عرض الفاتورة">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (group.pageId && group.invoiceId) {
                                window.open(`/invoices/${group.pageId}/${group.invoiceId}/view`, '_blank');
                              } else {
                                alert('معلومات الفاتورة غير متوفرة');
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedRows.has(group.invoiceNumber)} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              تفاصيل القيود ({group.entriesCount})
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>الحساب</TableCell>
                                  <TableCell>نوع الحساب</TableCell>
                                  <TableCell>نوع القيمة</TableCell>
                                  <TableCell align="right">المبلغ</TableCell>
                                  <TableCell>الحالة</TableCell>
                                  <TableCell align="center">الإجراءات</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.entries.map((entry) => (
                                  <TableRow key={entry._id}>
                                    <TableCell>
                                      {entry.accountId?.accountNumber} - {entry.accountId?.name || entry.accountName}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={entry.accountType === 'debit' ? 'مدين' : 'دائن'}
                                        size="small"
                                        color={entry.accountType === 'debit' ? 'primary' : 'secondary'}
                                      />
                                    </TableCell>
                                    <TableCell>{getValueTypeLabel(entry.valueType)}</TableCell>
                                    <TableCell align="right">
                                      {entry.amount.toLocaleString('ar-SA', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })} ر.س
                                    </TableCell>
                                    <TableCell>{getStatusChip(entry.status)}</TableCell>
                                    <TableCell align="center">
                                      <Tooltip title="عرض التفاصيل">
                                        <IconButton
                                          size="small"
                                          onClick={() => handleViewDetails(entry)}
                                        >
                                          <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      {entry.status === 'failed' && (
                                        <Tooltip title="إعادة المحاولة">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleRetry(entry._id)}
                                          >
                                            <RefreshIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title="حذف">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleDelete(entry._id)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredEntries.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="عدد الصفوف:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Dialog لعرض التفاصيل */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>تفاصيل القيد</DialogTitle>
        <DialogContent dividers>
          {selectedEntry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">رقم الفاتورة:</Typography>
                <Typography variant="body1">{selectedEntry.invoiceNumber}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">نوع الفاتورة:</Typography>
                <Typography variant="body1">{getInvoiceTypeLabel(selectedEntry.invoiceType)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">الحساب:</Typography>
                <Typography variant="body1">
                  {selectedEntry.accountId?.accountNumber} - {selectedEntry.accountId?.name || selectedEntry.accountName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">مصدر الحساب:</Typography>
                <Typography variant="body1">
                  {selectedEntry.accountSource === 'customer_supplier' ? 'حساب العميل/المورد' : 'حساب آخر'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">نوع الحساب:</Typography>
                <Typography variant="body1">
                  {selectedEntry.accountType === 'debit' ? 'مدين' : 'دائن'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">نوع القيمة:</Typography>
                <Typography variant="body1">{getValueTypeLabel(selectedEntry.valueType)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">المبلغ:</Typography>
                <Typography variant="body1">
                  {selectedEntry.amount.toLocaleString('ar-SA', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} ر.س
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">الحالة:</Typography>
                {getStatusChip(selectedEntry.status)}
              </Box>
              {selectedEntry.errorMessage && (
                <Box>
                  <Typography variant="subtitle2" color="error">رسالة الخطأ:</Typography>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {selectedEntry.errorMessage}
                  </Alert>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">تاريخ الإنشاء:</Typography>
                <Typography variant="body1">
                  {format(new Date(selectedEntry.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: ar })}
                </Typography>
              </Box>
              {selectedEntry.createdBy && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">المستخدم:</Typography>
                  <Typography variant="body1">{selectedEntry.createdBy.username}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceEntriesPage;